<template>
  <aside
    v-if="creation.tasks.value.length || creation.storageError.value"
    class="creation-panel"
    :aria-label="t('leafletCreation.title')"
  >
    <button
      v-if="creation.collapsed.value"
      class="creation-panel__toggle"
      :aria-expanded="false"
      aria-controls="leaflet-creation-tasks"
      @click="creation.collapsed.value = false"
    >
      <el-icon><DocumentAdd /></el-icon>
      {{ t('leafletCreation.title') }}
      <span class="creation-panel__count">{{ creation.activeCount.value }}</span>
      <span v-if="creation.attentionCount.value" class="creation-panel__attention">
        {{ creation.attentionCount.value }}
      </span>
      <el-icon><ArrowUp /></el-icon>
    </button>
    <template v-else>
      <header class="creation-panel__header">
        <h2>{{ t('leafletCreation.title') }}</h2>
        <el-button
          text
          :aria-label="t('leafletCreation.collapse')"
          :aria-expanded="true"
          aria-controls="leaflet-creation-tasks"
          @click="creation.collapsed.value = true"
        >
          <el-icon><Close /></el-icon>
        </el-button>
      </header>
      <p v-if="creation.storageError.value" role="alert" class="creation-panel__error">
        {{ t('leafletCreation.storageError') }}
      </p>
      <ul id="leaflet-creation-tasks" class="creation-panel__tasks">
        <li v-for="task in creation.tasks.value" :key="task.id" :data-state="task.state" class="creation-panel__task">
          <div class="creation-panel__content">
            <strong class="creation-panel__identity">{{ task.store.name }}</strong>
            <div class="creation-panel__name">{{ task.tab.naturalId }}</div>
            <p v-if="task.state !== 'done'" class="creation-panel__status">{{ taskLabel(task) }}</p>
            <p v-if="task.error" class="creation-panel__error">{{ translatedError(task.error) }}</p>
            <div class="creation-panel__actions">
              <el-button v-if="!isActive(task) && task.state !== 'done'" size="small" @click="retry(task.id)">
                {{ task.state === 'unknown' ? t('leafletCreation.checkStatus') : t('leafletCreation.retry') }}
              </el-button>
              <el-button
                v-if="['attention', 'files'].includes(task.state)"
                size="small"
                type="primary"
                plain
                @click="edit(task)"
              >
                {{ t('leafletCreation.edit') }}
              </el-button>
              <el-button v-if="!isActive(task)" text size="small" @click="dismiss(task)">
                {{ t('leafletCreation.dismiss') }}
              </el-button>
            </div>
          </div>
          <div class="creation-panel__indicator" role="img" :aria-label="taskLabel(task)">
            <el-icon v-if="task.state === 'done'" :size="24" class="creation-panel__success"><CircleCheck /></el-icon>
            <el-icon v-else-if="isActive(task)" :size="24" class="is-loading"><Loading /></el-icon>
            <el-icon v-else :size="24" class="creation-panel__warning"><Warning /></el-icon>
          </div>
        </li>
      </ul>
    </template>
    <span class="creation-panel__sr" role="status" aria-live="polite">
      {{
        t('leafletCreation.summary', { active: creation.activeCount.value, attention: creation.attentionCount.value })
      }}
    </span>
  </aside>
  <AddEditLeafletDialogContainer
    v-if="editing"
    :key="editing.id"
    :visible="true"
    dialog-status="create"
    :store="editing.store"
    :leaflet="null"
    :available-platforms="editing.platforms"
    :current-country="editing.context.country"
    :is-admin="isAdmin"
    :restored-tab="editing.tab"
    :resume-operation-id="editing.id"
    @on-close="editing = null"
  />
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowUp, CircleCheck, Close, DocumentAdd, Loading, Warning } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox, ElNotification } from 'element-plus'

import store from '@/store'

import { copyTab, type CreationTask } from './creationTask'
import { currentCreationContext, useLeafletCreationStore } from './useLeafletCreationStore'

const AddEditLeafletDialogContainer = defineAsyncComponent(
  () => import('@/views/dashboard/components/AddEditLeafletDialog/AddEditLeafletDialogContainer.vue')
)
const { t, te } = useI18n()
const creation = useLeafletCreationStore()
const editing = ref<CreationTask | null>(null)
const isAdmin = computed(() => (store.getters.roles || []).some((role: string) => ['ADMIN', 'ROOT'].includes(role)))
const knownStates = new Map<string, string>()

watch(
  () => [store.getters.name, store.getters.profile, store.getters.token],
  () => {
    editing.value = null
    knownStates.clear()
    creation.activate(currentCreationContext())
    creation.tasks.value.forEach(task => knownStates.set(task.id, task.state))
  },
  { immediate: true }
)

const unsubscribe = creation.subscribe(task => {
  const previous = knownStates.get(task.id)
  knownStates.set(task.id, task.state)
  if (previous === task.state) return
  if (['attention', 'unknown', 'files'].includes(task.state)) {
    ElNotification({
      title: task.store.name,
      message: t('leafletCreation.needsAttention', { name: task.tab.naturalId }),
      type: 'warning',
      duration: 5000
    })
  } else if (task.state === 'done') {
    ElNotification({
      title: task.store.name,
      message: t('leafletCreation.completed', { name: task.tab.naturalId }),
      type: 'success',
      duration: 4000
    })
  }
})

function isActive(task: CreationTask) {
  return ['queued', 'running', 'generating'].includes(task.state)
}

function taskLabel(task: CreationTask) {
  if (task.state === 'running') return t(`leafletCreation.steps.${task.step}`)
  const state = t(`leafletCreation.states.${task.state}`)
  return ['attention', 'files', 'unknown'].includes(task.state)
    ? `${state}: ${t(`leafletCreation.steps.${task.step}`)}`
    : state
}

function translatedError(error: string) {
  return te(`leafletCreation.errors.${error}`) ? t(`leafletCreation.errors.${error}`) : error
}

function edit(task: CreationTask) {
  editing.value = { ...task, tab: copyTab(task.tab) }
  if (task.error) editing.value.tab.failedMessage = translatedError(task.error)
}

async function retry(id: string) {
  try {
    await creation.retry(id)
  } catch {
    ElMessage.error(t('leafletCreation.cannotStart'))
  }
}

async function dismiss(task: CreationTask) {
  try {
    if (task.state !== 'done') await ElMessageBox.confirm(t('leafletCreation.dismissConfirm'), { type: 'warning' })
    await creation.dismiss(task.id)
  } catch {
    /* The operator can keep the entry by cancelling. */
  }
}

function beforeUnload(event: BeforeUnloadEvent) {
  if (!creation.hasClientWork.value) return
  event.preventDefault()
  event.returnValue = ''
}

function syncStorage(event: StorageEvent) {
  if (event.key?.startsWith('mg:leaflet-creation:v1:')) creation.refresh()
}

window.addEventListener('beforeunload', beforeUnload)
window.addEventListener('storage', syncStorage)
onBeforeUnmount(() => {
  unsubscribe()
  creation.dispose()
  window.removeEventListener('beforeunload', beforeUnload)
  window.removeEventListener('storage', syncStorage)
})
</script>

<style scoped lang="scss">
.creation-panel {
  position: fixed;
  // Reserve the 40px back-to-top button at right: 16px, plus a 16px gap.
  right: 72px;
  bottom: 16px;
  z-index: 1990;
  width: min(390px, calc(100vw - 84px));
  background: var(--el-bg-color, #fff);
  color: var(--el-text-color-primary);
  border: 1px solid var(--el-border-color);
  border-radius: 8px;
  box-shadow: 0 6px 28px rgb(0 0 0 / 15%);

  &__toggle {
    width: 100%;
    padding: 14px 16px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    border: 0;
    border-radius: inherit;
    background: inherit;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }
  &__toggle:focus-visible {
    outline: 2px solid var(--el-color-primary);
    outline-offset: 3px;
  }
  &__count,
  &__attention {
    min-width: 24px;
    border-radius: 12px;
    padding: 2px 6px;
    background: var(--el-fill-color);
    font-size: 12px;
  }
  &__attention {
    background: var(--el-color-warning-light-9);
    color: var(--el-color-warning-dark-2);
  }
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 16px;
    border-bottom: 1px solid var(--el-border-color-lighter);
  }
  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
  &__tasks {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: min(440px, 60vh);
    overflow-y: auto;
  }
  &__task {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--el-border-color-lighter);
  }
  &__task:last-child {
    border-bottom: 0;
  }
  &__content {
    flex: 1;
    min-width: 0;
  }
  &__indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 24px;
  }
  &__identity {
    display: block;
    font-size: 13px;
  }
  &__name {
    margin-top: 5px;
    overflow-wrap: anywhere;
    color: var(--el-text-color-secondary);
    font-size: 12px;
  }
  &__status {
    margin: 10px 0 0;
    font-size: 13px;
  }
  &__error {
    margin: 8px 0;
    font-size: 12px;
    overflow-wrap: anywhere;
    color: var(--el-color-danger-dark-2);
  }
  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  }
  &__actions:empty {
    display: none;
  }
  &__actions :deep(.el-button + .el-button) {
    margin-left: 0;
  }
  &__success {
    color: var(--el-color-success);
  }
  &__warning {
    color: var(--el-color-warning);
  }
  &__sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
}
@media (prefers-reduced-motion: reduce) {
  .creation-panel .is-loading {
    animation: none;
  }
}
</style>
